/**
 * `lazy()` que sobrevive a um deploy acontecido com a aba aberta.
 *
 * O problema real (06/08/2026, produção): o portal ficou aberto enquanto o front
 * foi deployado três vezes. Ao clicar em "criar documento", que carrega o editor
 * sob demanda, o navegador pediu o chunk do bundle ANTIGO, que não existe mais.
 *
 * E aí vem a parte traiçoeira: o host serve SPA, então **qualquer** caminho não
 * encontrado devolve `index.html` com `200 text/html`, inclusive
 * `/assets/js/KbDocForm-<hash>.js`. O navegador recebe HTML onde esperava um módulo,
 * falha ao parsear, o `import()` rejeita e o error boundary mostra "Erro na Página".
 * Nada no console diz "404", o sintoma não aponta para a causa.
 *
 * A saída é recarregar: o `index.html` novo referencia os hashes novos. Recarregar
 * é seguro aqui porque o chunk só falha ANTES de montar a tela, não há trabalho do
 * usuário em memória para perder.
 *
 * A trava de uma tentativa por chunk existe para o caso de a falha ser outra (asset
 * corrompido, rede caindo): sem ela, o reload viraria laço infinito e o usuário
 * ficaria preso numa tela piscando, que é pior do que a mensagem de erro.
 */

import { lazy } from 'react'

const CHAVE = 'kb:chunk-recarregado:'

/** A falha é "chunk sumiu" e não um erro dentro do próprio módulo? */
function pareceChunkObsoleto(erro: unknown): boolean {
  const msg =
    erro instanceof Error ? `${erro.name} ${erro.message}` : String(erro)
  return (
    /ChunkLoadError/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // Chrome/Safari ao receber HTML onde esperavam JS, o caso do fallback de SPA.
    /Unexpected token '<'/.test(msg) ||
    /expected expression, got '<'/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  )
}

/**
 * A regra, isolada do React para poder ser testada sem montar componente:
 * tenta carregar; se falhou por chunk obsoleto e ainda não tentamos, recarrega.
 *
 * Devolve uma promise **pendente para sempre** no caminho do reload, de propósito:
 * a página está saindo, e resolver ou rejeitar aqui faria a tela piscar um erro no
 * meio do caminho.
 */
export function carregarComRecarga<M>(
  id: string,
  carregar: () => Promise<M>,
  recarregar: () => void = () => window.location.reload()
): Promise<M> {
  return carregar().catch((erro: unknown) => {
    const chave = CHAVE + id
    if (pareceChunkObsoleto(erro) && sessionStorage.getItem(chave) !== '1') {
      sessionStorage.setItem(chave, '1')
      recarregar()
      return new Promise<M>(() => {})
    }
    sessionStorage.removeItem(chave)
    throw erro
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends React.ComponentType<any>>(
  id: string,
  carregar: () => Promise<{ default: T }>
) {
  return lazy(() => carregarComRecarga(id, carregar))
}

/** Chamar quando o app montar com sucesso: zera as travas da sessão. */
export function limparTravasDeChunk(): void {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const k = sessionStorage.key(i)
    if (k?.startsWith(CHAVE)) sessionStorage.removeItem(k)
  }
}

/**
 * Rede de segurança para o mesmo problema em módulos que o Vite **pré-carrega**
 * (`modulepreload`), inclusive o CSS. O `lazyWithReload` só cobre o que passa por
 * `import()` no nosso código; o preload falha antes disso e derruba o estilo da
 * página, que foi como os botões apareceram sem tamanho e com o texto vazando.
 *
 * O Vite emite `vite:preloadError` nesse caso. Chamar `preventDefault` impede que
 * ele vire um erro não tratado, e recarregamos uma vez para pegar o `index.html`
 * novo, com os hashes novos.
 */
export function instalarRecargaDePreload(): void {
  const CHAVE_PRELOAD = CHAVE + '__preload__'
  window.addEventListener('vite:preloadError', evento => {
    evento.preventDefault()
    if (sessionStorage.getItem(CHAVE_PRELOAD) === '1') return
    sessionStorage.setItem(CHAVE_PRELOAD, '1')
    window.location.reload()
  })
}
